import { forwardRef, Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LMLoggerService } from './lm-logger.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { WidgetSize } from 'ids-enterprise-ng';
import { AppConstants } from '@utils/app.constants';
import { LMContextService } from './lm-context.service';
import { LMUrlInfoBuilderService } from './lm-url-info-builder.service';
import { LMUrlUtilService } from './utils';
import { LMGlobalUtilService } from '@app/utils';

@Injectable()
export class LMSpecService {
  constructor(
    private http: HttpClient,
    // @Inject(forwardRef(() => LMGlobalUtilService)) private lmGlobalUtilService,
    // private lmGlobalUtilService: LMGlobalUtilService,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmUrlUtilService: LMUrlUtilService,
  ) {}

  /**
   * Return the bannerSpec for the given url.
   * @returns {Observable<LMBannerSpec>}
   */
  public getBannerSpec(url: string): Observable<LMBannerSpec> {
    const specUrl = this.buildSpecUrl(url);
    return <Observable<LMBannerSpec>> this.http.get(specUrl).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Return the bannerSpec for the given url.
   * @returns {Observable<LMApplicationMenuSpec>}
   */
  public getApplicationMenuSpec(url: string): Observable<LMApplicationMenuSpec> {
    const specUrl = this.buildSpecUrl(url);
    return <Observable<LMApplicationMenuSpec>> this.http.get(specUrl).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Return the navigationSpec for the given url.
   * @returns {Observable<LMActionSpec>}
   */
  public getActionSpec(url: string): Observable<LMActionSpec> {
    const actionUrl = this.buildSpecUrl(url);
    return <Observable<LMActionSpec>> this.http.get(actionUrl).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Return the navigationSpec for the given url.
   * @returns {Observable<LMNavigationSpec>}
   */
  public getNavigationSpec(url: string): Observable<LMNavigationSpec> {
    const navUrl = this.buildSpecUrl(url);
    return <Observable<LMNavigationSpec>> this.http.get(navUrl).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Return the drillSpec for the given url.
   * @returns
   */
  public getDrillSpec(url: string): Observable<LMDrillSpec> {
    const specUrl = this.buildSpecUrl(url);
    return <Observable<LMDrillSpec>> this.http.get(specUrl).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Returns captcha spec
   * @param url
   * @returns {Observable<LMCaptchaSpec>}
   */
  public getCaptchaSpec(url: string): Observable<LMCaptcha> {
    const specUrl = this.buildSpecUrl(url);
    return <Observable<LMCaptcha>> this.http.get(specUrl).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Return the pageUrlInfo and pageSpec for the given page url.
   * @returns
   */
  public getPageSpec(url: string): Observable<LMPageSpecResponse | LMSystemException> {
    return new Observable(observer => {

      url = this.lmUrlUtilService.updateUrlParams(url, this.lmUrlUtilService.addDataContextUrlParams({ }));
      const pageUrlInfo = this.lmUrlInfoBuilderService.getPageUrlInfoFromHttpUrl(url);

      this.http.get(pageUrlInfo.specUrl).subscribe(
        pageSpecResponse => {

          if ((pageSpecResponse as LMSystemException)
            && ((pageSpecResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
              || (pageSpecResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
            observer.next(pageSpecResponse as LMSystemException);
            observer.complete();
            return;
          }

          const pageSpec = pageSpecResponse as LMPageSpec;
          if (pageSpec.serverLog) {
            this.lmLoggerService.serverLog(pageSpec.serverLog);
          }
          this.lmUrlInfoBuilderService.fillOutPageUrls(pageUrlInfo, pageSpec);
          observer.next({ pageUrlInfo, pageSpec } as LMPageSpecResponse);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        });
    });

  }

  /**
   * Return the listUrlInfo and listSpec for the given url.
   * @returns
   */
  public getListSpec(url: string): Observable<LMListSpecResponse | LMSystemException> {
    return new Observable(observer => {
      url = this.lmUrlUtilService.updateUrlParams(url, this.lmUrlUtilService.addDataContextUrlParams({ }));
      const listUrlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromHttpUrl(url);

      this.http.get(listUrlInfo.specUrl).subscribe(
        listSpecResponse => {

          if ((listSpecResponse as LMSystemException)
        && ((listSpecResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
         || (listSpecResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
            observer.next(listSpecResponse as LMSystemException);
            observer.complete();
            return;
          }

          const listSpec = listSpecResponse as LMListSpec;
          if (listSpec.serverLog) {
            this.lmLoggerService.serverLog(listSpec.serverLog);
          }
          this.lmUrlInfoBuilderService.fillOutListUrls(listUrlInfo, listSpec);
          observer.next({ listUrlInfo, listSpec } as LMListSpecResponse);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        });
    });
  }

  /**
   * Return the formUrlInfo and formSpec for the given url.
   * @returns
   */
  public getFormSpec(url: string): Observable<LMFormSpecResponse | LMSystemException> {
    return new Observable(observer => {

      url = this.lmUrlUtilService.updateUrlParams(url, this.lmUrlUtilService.addDataContextUrlParams({ }));
      const formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromHttpUrl(url);

      this.http.get(formUrlInfo.specUrl).subscribe(
        formSpecResponse => {

          if ((formSpecResponse as LMSystemException)
        && ((formSpecResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
         || (formSpecResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
            observer.next(formSpecResponse as LMSystemException);
            observer.complete();
            return;
          }

          const formSpec = formSpecResponse as LMFormSpec;
          if (formSpec.serverLog) {
            this.lmLoggerService.serverLog(formSpec.serverLog);
          }
          this.lmUrlInfoBuilderService.fillOutFormUrls(formUrlInfo, formSpec);
          observer.next({ formUrlInfo, formSpec });
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        });
    });
  }

  // private _getFormSpec(specUrl: string): Observable<LMFormSpec | LMSystemException> {
  //   return this.http.get(specUrl).pipe(catchError(error => this.handleError(error)));
  // }

  /**
   * Gets the form execution url from a actionSpec.actionUrl. The form
   * execution url is needed to perform instance, delete, etc... actions.
   * @param actionUrl - The actionSpec url resolve against a dataView resourceId
   * @returns The redirect structure with the form transaction url
   */
  public getFormTransactionUrl(actionUrl: string): Observable<any> {
    return this.getRedirectUrl(actionUrl);
  }

  public getFormSpecFromRedirectActionUrl(actionUrl: string, stepUpActionResult?: LMActionResult): Observable<LMActionFormUrlResponse | LMSystemException> {
    return new Observable(observer => {
      this.getRedirectUrl(actionUrl, stepUpActionResult).subscribe(
        redirectResponse => {
          if (((redirectResponse as LMSystemException) as LMSystemException)
        && ((redirectResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
         || (redirectResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
            observer.next(redirectResponse as LMSystemException);
            observer.complete();
            return;
          }

          if (redirectResponse.mfaUrl) {
            // throws circular dependency error for global util
            // TODO: test in mingle
            // this.lmGlobalUtilService.navigate(redirectResponse.mfaUrl);
            window.location.href = redirectResponse.mfaUrl;
            return;
          }
          // todo spec-url-refactor
          const formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromHttpUrl(redirectResponse.redirectUrl);
          const resourceId = formUrlInfo.resource.dataResourceId; // used to create the initial data url
          const action = formUrlInfo.action;

          this.http.get(formUrlInfo.specUrl).subscribe(
            formSpecResponse => {

              if ((formSpecResponse as LMSystemException)
          && ((formSpecResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
           || (formSpecResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
                observer.next(formSpecResponse as LMSystemException);
                observer.complete();
                return;
              }

              const formSpec = formSpecResponse as LMFormSpec;
              this.lmUrlInfoBuilderService.fillOutFormUrls(formUrlInfo, formSpec);
              observer.next({ formUrlInfo, formSpec, resourceId, action });
              observer.complete();
            },
            error => {
              this.handleError(error);
              observer.error(error);
              observer.complete();
            });
        },
        error => {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        });
    });
  }

  /**
   * Used by menu definitions on page definitions.
   * @param actionUrl
   * @param url - pass in dialogUrl to get the form spec.
   * @returns
   */
  public getFormSpecFromDialogActionUrl(url: string): Observable<LMFormSpecResponse | LMSystemException> {
    return new Observable(observer => {

      url = this.lmUrlUtilService.updateUrlParams(url, this.lmUrlUtilService.addDataContextUrlParams({ }));
      const formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromHttpUrl(url);

      this.http.get(formUrlInfo.specUrl).subscribe(
        formSpecResponse => {
        // TODO: instead of returning LMSystemException, why don't we handle the error here and now?
        // everyone that will be receiving it will do the same anyway
          if ((formSpecResponse as LMSystemException)
        && ((formSpecResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
         || (formSpecResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
            observer.next(formSpecResponse as LMSystemException);
            observer.complete();
            return;
          }

          const formSpec = formSpecResponse as LMFormSpec;
          this.lmUrlInfoBuilderService.fillOutFormUrls(formUrlInfo, formSpec);
          observer.next({ formUrlInfo, formSpec });
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        });
    });
  }

  public getRedirectUrl(url: string, stepUpActionResult?: LMActionResult): Observable<any> {  // todo ppatton type the Observable response

    // url = this.lmUrlUtilService.updateUrlParams(url, this.lmUrlUtilService.addDataContextUrlParams({ }));
    if (!this.lmContextService.useNewSpecUrls()) {
      let urlParams: LMUrlParams = { 'csk.asofdate': this.lmContextService.getAsOfDate() };
      url = this.lmUrlUtilService.updateUrlParams(url, urlParams);
      urlParams = { 'csk.showdeletedrecords': this.lmContextService.getShowDeletedRecords() };
      url = this.lmUrlUtilService.updateUrlParams(url, urlParams);
    }

    if (stepUpActionResult) {
      return of(stepUpActionResult);
    }
    return this.http.get(url).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));

    // todo spec-url-refactor ben pwpatton any idea why this code fails a test in lm-spec.service.spec
    //   and the code above does not? See
    // return this.http.get(url).pipe(catchError(error => this.handleError(error)));
  }

  private handleError(error: Response) {
    // in a real world app, we may send the error to some remote logging infrastructure
    // instead of just logging it to the console
    this.lmLoggerService.trace(error);
    return throwError(error || 'Server error');
  }

  private buildSpecUrl(url: string): string {
    let urlParams = { output: 'spec' } as LMUrlParams;
    urlParams = this.lmUrlUtilService.addDataContextUrlParams(urlParams);
    return this.lmUrlUtilService.updateUrlParams(url, urlParams);
  }

  // todo ppatton move me to a more generic "util" service.
  //   consider new lm-spec-util.service.ts for generice spec utilities that don't
  //   belong in form-spec-util or list-spec-util services.
  public toWidgetSizeToType(size: number): WidgetSize {
    switch (size) {
      case 4: return 'quad';
      case 3: return 'triple';
      case 2: return 'double';
    }
    return 'single';
  }
}
